import React, { useState } from "react"
import { AlertTriangle, Loader2, Play, Settings2 } from "lucide-react"
import LearnerTopicTree from "../../Questions/components/LearnerTopicTree"
import LearnerField from "../../../../component/student/LearnerField"
import {
  MAX_PAPER_TITLE_LEN,
  MAX_QUESTION_COUNT,
  MIN_QUESTION_COUNT,
  QUESTION_COUNT_OPTIONS,
  clampQuestionCount,
  isValidQuestionCount,
} from "../types"
import UseAiContentToggle from "./UseAiContentToggle"
import ContextModeSelect from "./ContextModeSelect"
import { GRADE_LEVEL_OPTIONS } from "../utils/gradeLevels"
import "./ConfigureStep.style.scss"

interface ConfigureStepProps {
  subjectId: string
  subjectTitle: string
  levelLabels?: string[]
  selectedHierarchyIds: string[]
  selectedCount: number
  paperTitle: string
  paperGrade: string
  contextMode: string
  useAi: boolean
  isGenerating: boolean
  onBack: () => void
  onHierarchyChange: (ids: string[]) => void
  onSelectCount: (count: number) => void
  onPaperTitleChange: (title: string) => void
  onPaperGradeChange: (grade: string) => void
  onContextModeChange: (mode: string) => void
  onUseAiChange: (useAi: boolean) => void
  onGenerate: () => void
}

const isPresetCount = (count: number) =>
  QUESTION_COUNT_OPTIONS.some((o) => o.count === count)

const ConfigureStep = ({
  subjectId,
  subjectTitle,
  levelLabels,
  selectedHierarchyIds,
  selectedCount,
  paperTitle,
  paperGrade,
  contextMode,
  useAi,
  isGenerating,
  onBack,
  onHierarchyChange,
  onSelectCount,
  onPaperTitleChange,
  onPaperGradeChange,
  onContextModeChange,
  onUseAiChange,
  onGenerate,
}: ConfigureStepProps) => {
  const hasSelection = selectedHierarchyIds.length > 0
  const [countMode, setCountMode] = useState<"preset" | "custom">(() =>
    isPresetCount(selectedCount) ? "preset" : "custom"
  )
  const [customDraft, setCustomDraft] = useState(() =>
    isPresetCount(selectedCount) ? "" : String(selectedCount)
  )

  const draftTrimmed = customDraft.trim()
  const draftNumber = Number(draftTrimmed)
  const customInvalid =
    countMode === "custom" &&
    draftTrimmed !== "" &&
    !isValidQuestionCount(draftNumber)
  const customEmpty = countMode === "custom" && draftTrimmed === ""
  const titleTrimmed = paperTitle.trim()
  const canGenerate =
    hasSelection &&
    Boolean(titleTrimmed) &&
    Boolean(paperGrade) &&
    !customInvalid &&
    !customEmpty &&
    isValidQuestionCount(selectedCount)

  const selectPreset = (count: number) => {
    setCountMode("preset")
    setCustomDraft("")
    onSelectCount(count)
  }

  const onCustomChange = (raw: string) => {
    // Digits only while typing — keeps "50" entry stable.
    const next = raw.replace(/[^\d]/g, "")
    setCountMode("custom")
    setCustomDraft(next)

    if (next === "") return

    const parsed = Number(next)
    // Commit only complete valid values — never clamp mid-edit.
    if (Number.isInteger(parsed) && isValidQuestionCount(parsed)) {
      onSelectCount(parsed)
    }
  }

  const onCustomBlur = () => {
    if (countMode !== "custom") return

    const trimmed = customDraft.trim()
    if (trimmed === "") {
      // Leave empty; do not auto-insert 1.
      return
    }

    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) {
      setCustomDraft(String(selectedCount))
      return
    }

    const next = clampQuestionCount(parsed)
    setCustomDraft(String(next))
    onSelectCount(next)
  }

  return (
    <div className="configure-step">
      <div className="configure-step__crumb">
        <button type="button" onClick={onBack}>
          ← Back
        </button>
        <span>/</span>
        <span className="configure-step__chip">{subjectTitle}</span>
      </div>

      <h2>
        <Settings2 size={20} />
        Configure Paper
      </h2>

      <div className="configure-step__sections">
        <section>
          <LearnerField
            id="practice-paper-title"
            label="Paper title"
            name="title"
            value={paperTitle}
            placeholder="e.g. Algebra quiz"
            maxLength={MAX_PAPER_TITLE_LEN}
            disabled={isGenerating}
            autoFocus
            required
            onChange={(event) => onPaperTitleChange(event.target.value)}
          />
          <p className="configure-step__hint">
            {paperTitle.length}/{MAX_PAPER_TITLE_LEN}
          </p>
          {!titleTrimmed && (
            <p className="configure-step__warn">
              <AlertTriangle size={14} /> Give this paper a name so you can
              find it later.
            </p>
          )}
        </section>

        <section>
          <div className="learner-field">
            <label
              htmlFor="practice-paper-level"
              className="learner-field__label"
            >
              Level
            </label>
            <div className="learner-field__control">
              <select
                id="practice-paper-level"
                name="grade"
                className="learner-field__input learner-field__select"
                value={paperGrade}
                disabled={isGenerating}
                required
                onChange={(event) => onPaperGradeChange(event.target.value)}
              >
                <option value="">Select level</option>
                {GRADE_LEVEL_OPTIONS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {!paperGrade && (
            <p className="configure-step__warn">
              <AlertTriangle size={14} /> Select a level. Key stage is set
              from this automatically.
            </p>
          )}
        </section>

        <section>
          <ContextModeSelect
            value={contextMode}
            disabled={isGenerating}
            onChange={onContextModeChange}
          />
        </section>

        <section>
          <div className="configure-step__topics-head">
            <label>Topics (Hierarchical)</label>
            <button
              type="button"
              className="is-muted"
              onClick={() => onHierarchyChange([])}
              disabled={!hasSelection}
            >
              Clear
            </button>
          </div>

          <div className="configure-step__tree">
            <LearnerTopicTree
              subjectId={subjectId}
              levelLabels={levelLabels}
              selectedIds={selectedHierarchyIds}
              onChange={onHierarchyChange}
            />
          </div>

          {!hasSelection && (
            <p className="configure-step__warn">
              <AlertTriangle size={14} /> Select at least one topic (any level)
              to generate a paper.
            </p>
          )}
        </section>

        <section>
          <label className="configure-step__count-label">
            Number of Questions
          </label>
          <div className="configure-step__counts">
            {QUESTION_COUNT_OPTIONS.map((opt) => (
              <button
                key={opt.count}
                type="button"
                className={
                  countMode === "preset" && selectedCount === opt.count
                    ? "is-on"
                    : undefined
                }
                onClick={() => selectPreset(opt.count)}
              >
                <div className="configure-step__count-num">{opt.label}</div>
                <div className="configure-step__count-sub">{opt.sub}</div>
              </button>
            ))}

            <label
              className={`configure-step__count-custom ${
                countMode === "custom" ? "is-on" : ""
              }`}
              htmlFor="practice-paper-custom-count"
            >
              <input
                id="practice-paper-custom-count"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="—"
                value={customDraft}
                aria-label="Custom number of questions"
                aria-invalid={customInvalid}
                onFocus={() => setCountMode("custom")}
                onChange={(e) => onCustomChange(e.target.value)}
                onBlur={onCustomBlur}
              />
              <span className="configure-step__count-sub">Custom</span>
            </label>
          </div>
          {customInvalid && (
            <p className="configure-step__warn">
              <AlertTriangle size={14} /> Enter a whole number between{" "}
              {MIN_QUESTION_COUNT} and {MAX_QUESTION_COUNT}.
            </p>
          )}
          {customEmpty && (
            <p className="configure-step__warn">
              <AlertTriangle size={14} /> Enter how many questions you want.
            </p>
          )}
        </section>
      </div>

      <div className="configure-step__actions">
        <UseAiContentToggle
          checked={useAi}
          disabled={isGenerating}
          onChange={onUseAiChange}
        />
        <button
          type="button"
          className="configure-step__generate"
          onClick={onGenerate}
          disabled={isGenerating || !canGenerate}
        >
          {isGenerating ? (
            <>
              <Loader2 size={16} className="is-spinning" /> Generating{" "}
              {selectedCount} Questions…
            </>
          ) : (
            <>
              <Play size={16} /> Generate {selectedCount}-Question Paper
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default ConfigureStep
